import {
  calculateTotalPricingBySpecialty,
  extractUniqueSpecialties,
  groupCptCodesBySpecialty,
  sortSpecialtiesByPricing,
} from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useMemo } from "react";
import { useGetPrecalculatedAllowablesQuery } from "store/queries/pricing";
import styled from "styled-components";

import { ErrorMessage } from "./ErrorMessage";
import { NoDataMessage } from "./NoDataMessage";
import { SpecialtyTabContent } from "./SpecialtyTabContent";
import { ILineItem } from "../../types/claim";

interface IPrecalculatedAllowablesContentProps {
  accountId: string;
  payerId: number;
  caseSignature: string;
  skip?: boolean; // Optional skip flag for controlling when to fetch
  lineItems?: ILineItem[]; // Optional line items from the claim
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const MissingCodesSection = styled.div`
  padding: 16px 20px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  margin-bottom: 16px;
`;

const MissingCodesTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
`;

const MissingCodesText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const ClaimAllowablesSection = styled.div`
  padding: 12px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
  max-width: 500px;
`;

const ClaimAllowablesTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

/**
 * Format specialty name from CONSTANT_CASE to Title Case
 * E.g., "PRIMARY_PROVIDER" -> "Primary Provider"
 */
function formatSpecialtyName(specialty: string): string {
  return specialty
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Reusable content component for displaying precalculated allowables data
 * Can be used standalone or within a modal
 */
export function PrecalculatedAllowablesContent({
  accountId,
  payerId,
  caseSignature,
  skip = false,
  lineItems = [],
}: IPrecalculatedAllowablesContentProps) {
  // Fetch data using RTK Query hook
  const { data, isLoading, error, refetch } =
    useGetPrecalculatedAllowablesQuery(
      {
        accountId,
        payerId,
        caseSignature,
      },
      {
        skip: skip || !accountId || !payerId || !caseSignature,
      }
    );

  // Handler for retry button
  const handleRetry = () => {
    refetch();
  };

  // Transform data: extract specialties, group by specialty, calculate pricing, sort
  const { sortedSpecialties, specialtyGroupedData, totalAllowable } =
    useMemo(() => {
      if (!data?.data?.calculatedData) {
        return {
          sortedSpecialties: [],
          specialtyGroupedData: new Map(),
          totalAllowable: 0,
        };
      }

      const { calculatedData } = data.data;
      const specialties = extractUniqueSpecialties(calculatedData);
      const grouped = groupCptCodesBySpecialty(calculatedData);
      const pricing = calculateTotalPricingBySpecialty(calculatedData);
      const sorted = sortSpecialtiesByPricing(specialties, pricing);

      // Calculate total allowable across ALL specialties
      // For each CPT code, group by specialty and use only the HIGHEST-priced provider per specialty
      const total = calculatedData.cptCodes.reduce((sum, cptCode) => {
        const procedures = cptCode.providerProcedures || [];

        // Group procedures by specialty
        const bySpecialty = new Map<string, typeof procedures>();
        procedures.forEach((pp) => {
          const { specialty } = pp.provider;
          if (!bySpecialty.has(specialty)) {
            bySpecialty.set(specialty, []);
          }
          bySpecialty.get(specialty)!.push(pp);
        });

        // For each specialty, find the highest-priced provider
        let cptTotal = 0;
        bySpecialty.forEach((specialtyProviders) => {
          const highestPriced = specialtyProviders.reduce((max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          });
          cptTotal += highestPriced.finalAmountInCents;
        });

        return sum + cptTotal;
      }, 0);

      return {
        sortedSpecialties: sorted,
        specialtyGroupedData: grouped,
        totalAllowable: total,
      };
    }, [data]);

  // Format currency helper
  const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <>
      {isLoading && (
        <LoadingContainer>
          <ProgressSpinner />
        </LoadingContainer>
      )}

      {/* Error state: API request failed */}
      {!isLoading && error && (
        <ErrorMessage error={error} onRetry={handleRetry} />
      )}

      {/* No data state: API returned successfully but no data or empty specialties */}
      {!isLoading && !error && (!data || sortedSpecialties.length === 0) && (
        <NoDataMessage />
      )}

      {/* Success state: Display total and tabs with data */}
      {!isLoading && !error && data && sortedSpecialties.length > 0 && (
        <>
          {/* Total Allowable Section */}
          <TotalAllowableSection>
            <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
            <TotalAllowableAmount>
              {formatCurrency(totalAllowable)}
            </TotalAllowableAmount>
          </TotalAllowableSection>

          {/* Missing Codes Warning (if any) */}
          {data.data.missingCodes && data.data.missingCodes.length > 0 && (
            <MissingCodesSection>
              <MissingCodesTitle>
                ⚠️ Incomplete Calculation - Missing Procedure Codes
              </MissingCodesTitle>
              <MissingCodesText>
                The following procedure codes from the case signature were not
                found in our database and are NOT included in the total
                allowable above:{" "}
                <strong>
                  {data.data.missingCodes
                    .map((mc) => `${mc.code} (qty: ${mc.quantity})`)
                    .join(", ")}
                </strong>
              </MissingCodesText>
            </MissingCodesSection>
          )}

          {/* Specialty Tabs */}
          <TabView>
            {sortedSpecialties.map((specialty) => {
              const cptCodes = specialtyGroupedData.get(specialty) || [];
              return (
                <TabPanel
                  key={specialty}
                  header={formatSpecialtyName(specialty)}
                >
                  <SpecialtyTabContent
                    cptCodes={cptCodes}
                    specialty={specialty}
                  />
                </TabPanel>
              );
            })}
          </TabView>

          {/* Claim Allowables Table */}
          {lineItems && lineItems.length > 0 && (
            <ClaimAllowablesSection>
              <ClaimAllowablesTitle>Claim Allowables</ClaimAllowablesTitle>
              <DataTable
                value={lineItems}
                stripedRows
                responsiveLayout="scroll"
                style={{ fontSize: "0.9rem" }}
              >
                <Column
                  field="cptCode"
                  header="Code"
                  style={{ minWidth: "8rem" }}
                />
                <Column
                  field="modifier"
                  header="Modifier"
                  body={(rowData) => rowData.modifier || "-"}
                  style={{ minWidth: "8rem" }}
                />
                <Column
                  field="lineAllowed"
                  header="Allowed Amount"
                  body={(rowData) =>
                    rowData.lineAllowed != null
                      ? formatCurrency(Math.round(rowData.lineAllowed * 100))
                      : "-"
                  }
                  style={{ minWidth: "10rem" }}
                />
              </DataTable>
            </ClaimAllowablesSection>
          )}
        </>
      )}
    </>
  );
}
